import { GetViewerAvailableProgramsQuery } from '@sandtech-hub-monorepo/ehub-client';
import { useMemo, useState } from 'react';
import { Button, Card } from '@sandtech-hub-monorepo/ui-kit';
import { CalendarIcon, DotIcon, RefreshCwIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatDate } from '../../dateFormat';
import { ProgramEnrollmentModal } from './ProgramEnrollmentModal';
import { enrollButtonState } from '../enrollButtonState';
import { CourseCardType } from './ProgramCourseCard';
import { useGetMaybeViewer } from '@sandtech-hub-monorepo/ui-auth-kit';

export const ProgramCard = ({
  name,
  abbreviation,
  description,
  potentialProgramJourney,
  marketing,
  isEnrolled,
  wasEnrolled,
  canEnroll,
  programPageLink,
  learningPageLink,
  isUniversityProgram,
  requiresTuitionQuestionnaire,
  ...program
}: NonNullable<
  GetViewerAvailableProgramsQuery['viewer']['availablePrograms']
>[number] & { programPageLink: string; learningPageLink: string }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const { viewer } = useGetMaybeViewer();

  const fullProgram = {
    name,
    abbreviation,
    description,
    potentialProgramJourney,
    marketing,
    isEnrolled,
    wasEnrolled,
    canEnroll,
    isUniversityProgram,
    requiresTuitionQuestionnaire,
    ...program,
  };

  const startDate = useMemo(
    () =>
      potentialProgramJourney!.startDate
        ? new Date(potentialProgramJourney!.startDate)
        : undefined,
    [potentialProgramJourney]
  );

  const buttonState = enrollButtonState(
    isEnrolled,
    program.isGraduated,
    canEnroll,
    isUniversityProgram
  );

  return (
    <>
      <Card className="rounded-lg border-border-secondary overflow-hidden flex flex-col">
        <img
          src={marketing.thumbnail || ''}
          className="h-[200px] object-cover"
        />
        <div className="pt-3 pb-6 px-4 flex flex-col gap-1 flex-1">
          <div className="font-semibold">{name}</div>
          <div className="text-popover-foreground text-sm line-clamp-2 overflow-hidden text-ellipsis">
            {description}
          </div>
          <div className="flex-1" />
          <div className="text-foreground flex items-center gap-1 text-sm">
            {startDate && (
              <>
                <CalendarIcon size={16} className="text-popover-foreground" />
                Start Date: {formatDate(startDate)}
              </>
            )}
            {!startDate && isEnrolled && (
              <>
                <div className="text-warning">{CourseCardType.ONGOING}</div>
                <RefreshCwIcon className="text-warning" />
              </>
            )}
            <DotIcon />
            <div>{marketing.programDuration}</div>
          </div>
          <div className="flex gap-4 mt-5">
            <Link
              to={programPageLink.replace(':programId', abbreviation)}
              className="flex-1"
            >
              <Button variant="outline" className=" w-full">
                View Details
              </Button>
            </Link>
            <Button
              variant="enhanced"
              className="flex-1"
              disabled={buttonState.disabled}
              onClick={() => setModalOpen(true)}
            >
              {buttonState.ctaText}
            </Button>
          </div>
        </div>
      </Card>
      <ProgramEnrollmentModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        program={fullProgram}
        learningPageLink={learningPageLink}
        viewer={viewer}
      />
    </>
  );
};
